import {
  ChangeDetectionStrategy,
  Component,
  ViewEncapsulation,
  input,
  computed,
  inject,
  effect,
  signal,
} from '@angular/core';

import { InputComponent } from '../input/input.component';
import { FormControl, ReactiveFormsModule, ValidationErrors } from '@angular/forms';
import { ValidationErrorMessageService } from '../../../services';
import { SelectComponent, SelectOption } from '../select/select.component';

@Component({
  selector: 'orbit-form-field',
  standalone: true,
  imports: [InputComponent, ReactiveFormsModule, SelectComponent],
  templateUrl: './form-field.component.html',
  styleUrl: './form-field.component.scss',
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FormFieldComponent {
  private readonly validationErrorService = inject(ValidationErrorMessageService);

  label = input<string>();
  validationLabel = input<string>();
  inputType = input<'text' | 'number' | 'email' | 'password' | 'textarea' | 'select'>('text');
  name = input<string>('');
  autocomplete = input<boolean>(true);
  placeholder = input<string>();
  defaultController = new FormControl<string | null>(null);
  controller = input<FormControl>(this.defaultController);
  disabled = input<boolean>(false);
  testId = input<string>();
  errorMessage = input<string | undefined>(undefined);
  errorStyle = input<'tooltip' | 'inline'>('tooltip');
  selectOptions = input<SelectOption[] | undefined>([]);

  // Signal to force reactivity for FormControl changes
  formControlState = signal<{
    value: string | null;
    errors: ValidationErrors | null;
    invalid: boolean;
    dirty: boolean;
    touched: boolean;
  }>({
    value: null,
    errors: null,
    invalid: false,
    dirty: false,
    touched: false,
  });

  constructor() {
    // Effect to track FormControl changes and update signal
    effect(() => {
      const controller = this.controller();

      const updateState = () => {
        this.formControlState.set({
          value: controller.value,
          errors: controller.errors,
          invalid: controller.invalid,
          dirty: controller.dirty,
          touched: controller.touched,
        });
      };

      // Subscribe to both value changes AND status changes
      const valueSubscription = controller.valueChanges.subscribe(() => {
        updateState();
      });

      const statusSubscription = controller.statusChanges.subscribe(() => {
        updateState();
      });

      // Also update immediately with current state
      updateState();

      // Cleanup subscriptions when effect re-runs
      return () => {
        valueSubscription.unsubscribe();
        statusSubscription.unsubscribe();
      };
    });
  }

  // Method to manually trigger state update (can be called from blur event)
  updateFormControlState() {
    const controller = this.controller();
    this.formControlState.set({
      value: controller.value,
      errors: controller.errors,
      invalid: controller.invalid,
      dirty: controller.dirty,
      touched: controller.touched,
    });
  }

  // Handle blur event from orbit-input
  onInputBlur() {
    this.updateFormControlState();
  }

  /**
   * Computed property that automatically generates error messages from validation errors.
   * If an explicit errorMessage is provided, it takes precedence.
   * Otherwise, it uses the ValidationErrorMessageService to generate appropriate error messages.
   */
  displayErrorMessage = computed(() => {
    const explicitErrorMessage = this.errorMessage();
    if (explicitErrorMessage) {
      return explicitErrorMessage;
    }

    // Use the signal to ensure proper reactivity
    const controlState = this.formControlState();

    if (controlState.errors) {
      // Use validationLabel if provided, otherwise use label but strip asterisks
      // Use atomic pattern to avoid ReDoS: match optional spaces, asterisk, then trim the result
      const fieldLabel =
        this.validationLabel() ??
        this.label()
          ?.replace(/\s*\*$/, '')
          .trim() ??
        '';
      return this.validationErrorService.getErrorMessage(controlState.errors, fieldLabel);
    }

    return null;
  });
}
